import React, { useState } from "react";
import { buildApiUrl } from "../config/api";

const priorityLabelMap = {
    high: "高优",
    medium: "中优",
    low: "低优"
};

const GoalCard = ({ goal, isEditing, onEdit, onCancelEdit, onDelete, onUpdate }) => {
    const [editData, setEditData] = useState({
        title: goal.title,
        description: goal.description || "",
        priority: goal.priority,
        category: goal.category || ""
    });
    const [loading, setLoading] = useState(false);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setEditData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdate = async (event) => {
        event.preventDefault();
        setLoading(true);

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(buildApiUrl(`/api/goals/${goal.id}`), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    title: editData.title.trim(),
                    description: editData.description.trim() || null,
                    priority: editData.priority,
                    category: editData.category.trim() || null
                })
            });

            const data = await response.json();

            if (response.ok) {
                onUpdate(data.goal);
            } else {
                console.error("Failed to update goal:", data.error);
            }
        } catch (error) {
            console.error("Error updating goal:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("确定要删除这条成果记录吗？")) {
            return;
        }

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(buildApiUrl(`/api/goals/${goal.id}`), {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.ok) {
                onDelete();
            } else {
                console.error("Failed to delete goal");
            }
        } catch (error) {
            console.error("Error deleting goal:", error);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("zh-CN", {
            year: "numeric",
            month: "short",
            day: "numeric"
        });
    };

    if (isEditing) {
        return (
            <article className="achievement-card achievement-card--editing">
                <form className="achievement-edit" onSubmit={handleUpdate}>
                    <div className="achievement-edit__field">
                        <label htmlFor={`goal-title-${goal.id}`}>成果标题</label>
                        <input
                            id={`goal-title-${goal.id}`}
                            type="text"
                            name="title"
                            className="achievement-edit__input"
                            value={editData.title}
                            onChange={handleInputChange}
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="achievement-edit__field">
                        <label htmlFor={`goal-description-${goal.id}`}>描述与亮点</label>
                        <textarea
                            id={`goal-description-${goal.id}`}
                            name="description"
                            className="achievement-edit__textarea"
                            value={editData.description}
                            onChange={handleInputChange}
                            placeholder="补充过程、阻塞与复盘（选填）"
                            disabled={loading}
                        />
                    </div>

                    <div className="achievement-edit__row">
                        <div className="achievement-edit__field">
                            <label htmlFor={`goal-priority-${goal.id}`}>优先级</label>
                            <select
                                id={`goal-priority-${goal.id}`}
                                name="priority"
                                className="achievement-edit__select"
                                value={editData.priority}
                                onChange={handleInputChange}
                                disabled={loading}
                            >
                                <option value="high">高优</option>
                                <option value="medium">中优</option>
                                <option value="low">低优</option>
                            </select>
                        </div>
                        <div className="achievement-edit__field">
                            <label htmlFor={`goal-category-${goal.id}`}>标签</label>
                            <input
                                id={`goal-category-${goal.id}`}
                                type="text"
                                name="category"
                                className="achievement-edit__input"
                                value={editData.category}
                                onChange={handleInputChange}
                                placeholder="产品 / 学习 / 生活"
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="achievement-edit__actions">
                        <button
                            type="button"
                            className="achievement-edit__cancel"
                            onClick={onCancelEdit}
                            disabled={loading}
                        >
                            取消
                        </button>
                        <button
                            type="submit"
                            className="achievement-edit__save"
                            disabled={loading || !editData.title.trim()}
                        >
                            {loading ? "保存中..." : "保存"}
                        </button>
                    </div>
                </form>
            </article>
        );
    }

    const normalizedPriority = (goal.priority || "medium").toLowerCase();
    const priorityClass = `priority-chip priority-chip--${normalizedPriority}`;
    const priorityLabel = priorityLabelMap[normalizedPriority] || "优先级";

    return (
        <article className="achievement-card">
            <header className="achievement-card__header">
                <div className="achievement-meta">
                    <span className={priorityClass}>{priorityLabel}</span>
                    {goal.category && <span className="category-chip">{goal.category}</span>}
                </div>
                <div className="achievement-actions">
                    <button type="button" className="achievement-button" onClick={onEdit} title="编辑">
                        ✏️
                    </button>
                    <button type="button" className="achievement-button" onClick={handleDelete} title="删除">
                        ×
                    </button>
                </div>
            </header>

            <div>
                <h3 className="achievement-card__title">{goal.title}</h3>
                {goal.description && (
                    <p className="achievement-card__description">{goal.description}</p>
                )}
            </div>

            <footer className="achievement-card__footer">
                <span>
                    完成时间：
                    {goal.completed_at
                        ? formatDate(goal.completed_at)
                        : formatDate(goal.created_at)}
                </span>
                {goal.is_completed && <span className="achievement-card__status">✅ 已完成</span>}
            </footer>
        </article>
    );
};

export default GoalCard;
